'use client'

import { Button, Cell, InputText, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { camelCase } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { getFingerprint } from '@marketplace-web/session-management/audit-util'
import { useDataDomeCaptcha } from '@marketplace-web/bot-detection/data-dome-feature'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { changePassword, clickEvent } from '@marketplace-web/security-settings/change-password-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import ChangePasswordRestrictedModal from '../../components/ChangePasswordRestrictedModal'
import { isPasswordChangeRestricted } from '../../utils/errors'
import { ChangePasswordFormField } from './constants'
import { ChangePasswordFormModel } from './types'
import { isValueInObject } from '../../utils/object'

type Props = {
  instructions: string
}

const ChangePassword = ({ instructions }: Props) => {
  const {
    setError,
    handleSubmit,
    getValues,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm<ChangePasswordFormModel>({
    mode: 'onSubmit',
  })
  const getErrorMessage = useFormValidationMessage(errors, 'change_password.fields')
  const translate = useTranslate('change_password')
  const { user } = useSession()
  const breakpoints = useBreakpoint()
  const containerClass = classNames({ 'u-flexbox': breakpoints.desktops })
  const [isSuccess, setIsSuccess] = useState(false)
  const isCurrentPasswordVisible = !user?.is_login_via_external_system_only
  const [showPasswordChangeRestrictedModal, setShowPasswordChangeRestrictedModal] = useState(false)
  const { track } = useTracking()

  useEffect(() => {
    if (isSuccess) {
      reset()
    }
  }, [isSuccess, reset])

  const setErrors = (validationErrors: Array<ErrorItem>) => {
    validationErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, ChangePasswordFormField)) return

      setError(fieldName, { type: 'manual', message: value })
    })
  }

  const togglePasswordChangeRestrictedModal = () => {
    setShowPasswordChangeRestrictedModal(!showPasswordChangeRestrictedModal)
  }

  const handleFormSubmit = async (formData: ChangePasswordFormModel) => {
    if (!user?.id) return

    track(
      clickEvent({
        screen: 'change_password',
        target: 'submit_password_change',
      }),
    )

    const response = await changePassword({
      userId: user.id,
      currentPassword: formData.oldPassword,
      newPassword: formData.password,
      fingerprint: await getFingerprint(),
    })

    if (isPasswordChangeRestricted(response)) {
      togglePasswordChangeRestrictedModal()

      return
    }

    if ('errors' in response) {
      setErrors(response.errors)

      return
    }

    setIsSuccess(true)
  }

  useDataDomeCaptcha(() => {
    handleFormSubmit(getValues())
  })

  const renderCurrentPasswordField = () => (
    <Controller
      defaultValue=""
      control={control}
      name={ChangePasswordFormField.CurrentPassword}
      rules={{ required: true }}
      render={({ field }) => (
        <InputText
          {...field}
          placeholder={translate('fields.old_password.title')}
          type="password"
          validation={renderValidation(getErrorMessage(ChangePasswordFormField.CurrentPassword))}
        />
      )}
    />
  )

  const renderNewPasswordField = () => (
    <Controller
      defaultValue=""
      control={control}
      name={ChangePasswordFormField.NewPassword}
      rules={{ required: true }}
      render={({ field }) => (
        <InputText
          {...field}
          placeholder={translate('fields.password.title')}
          type="password"
          validation={renderValidation(getErrorMessage(ChangePasswordFormField.NewPassword))}
        />
      )}
    />
  )

  const renderConfirmPasswordField = () => {
    const getPasswordConfirmationErrorMessage = getErrorMessage(
      ChangePasswordFormField.RepeatPassword,
      {
        validate: translate('fields.repeat_password.errors.validate', {
          field: translate('fields.password.title'),
        }),
      },
    )

    return (
      <Controller
        defaultValue=""
        control={control}
        name={ChangePasswordFormField.RepeatPassword}
        rules={{
          required: true,
          validate: value => value === getValues().password,
        }}
        render={({ field }) => (
          <InputText
            {...field}
            placeholder={translate('fields.repeat_password.title')}
            type="password"
            validation={renderValidation(getPasswordConfirmationErrorMessage)}
          />
        )}
      />
    )
  }

  const renderSubmitButton = () => (
    <Button
      text={translate('actions.submit')}
      type="submit"
      styling="filled"
      disabled={isSubmitting || isSuccess}
      isLoading={isSubmitting}
      testId="change-password-button"
      onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (isSubmitting) {
          event.preventDefault()
        }
      }}
    />
  )

  const renderForm = () => (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {isCurrentPasswordVisible && renderCurrentPasswordField()}
      {renderNewPasswordField()}
      {renderConfirmPasswordField()}
      {renderSubmitButton()}
    </form>
  )

  const renderSuccessMessage = () => {
    if (!isSuccess) return null

    return (
      <>
        <Spacer size="large" />
        <Cell theme="success" title={translate('success_message.body')} />
      </>
    )
  }

  return (
    <>
      {showPasswordChangeRestrictedModal && (
        <ChangePasswordRestrictedModal onClose={togglePasswordChangeRestrictedModal} />
      )}
      <Text as="h1" text={translate('title')} type="heading" />
      {renderSuccessMessage()}
      <Spacer size="large" />
      <div className="u-clearfix">
        <div className={containerClass}>
          <Cell>
            <Text as="h2" text={translate('description')} type="title" />
            <Text as="span" text={instructions} html />
          </Cell>
          <Cell>{renderForm()}</Cell>
        </div>
      </div>
    </>
  )
}

export default ChangePassword
